/**
 * Turning a catalogue entry into the string a screen shows.
 *
 * Three steps, kept out of the React provider so they can be tested without
 * mounting anything: find the entry by its dotted key, pick the right form when
 * the entry has more than one, and fill in the variables.
 */

export type Vars = Record<string, string | number>;

/** Walk a dotted key. Returns whatever is at the end - a leaf string, a plural
 *  object, or a whole branch if the key was wrong - and lets the caller judge. */
export function getByPath(obj: unknown, path: string): unknown {
  return path.split(".").reduce<unknown>((acc, key) => {
    if (acc && typeof acc === "object" && key in (acc as Record<string, unknown>)) {
      return (acc as Record<string, unknown>)[key];
    }
    return undefined;
  }, obj);
}

/**
 * A message is normally a plain string. Where a language has to agree with a
 * count it may instead be `{ one, other }`, chosen by the `n` variable.
 *
 * Both catalogues need it. English needs it the obvious way: "1 characters" is
 * wrong, and a screen that counts anything reaches 1 constantly. Estonian needs
 * it too, which this file once claimed it did not: where a numeral governs a noun
 * directly, a count of 1 puts that noun in the nominative singular and every
 * other count puts it in the partitive singular. The wording only holds still
 * where the counted noun sits in an oblique case, or where the numeral governs
 * no noun at all - and those entries spell both forms out anyway, identically,
 * so that the two catalogues stay the same shape. `catalogues.test.ts` enforces
 * that: a key that is `{ one, other }` on one side must be so on the other.
 *
 * Anything other than exactly 1 takes `other`, zero included, which is what
 * English wants ("0 characters"). Counts under a different variable name are
 * not pluralised; name the counted thing `n` if the string has to agree with it.
 *
 * Returns undefined for anything that is not a usable string, which is how the
 * caller's fallback chain knows to try the next locale rather than rendering a
 * branch object as text.
 */
export function pluralize(msg: unknown, vars?: Vars): string | undefined {
  if (typeof msg === "string") return msg;
  if (!msg || typeof msg !== "object") return undefined;
  const forms = msg as { one?: unknown; other?: unknown };
  const pick = Number(vars?.n) === 1 && typeof forms.one === "string" ? forms.one : forms.other;
  return typeof pick === "string" ? pick : undefined;
}

/** An unknown placeholder is left standing rather than blanked: a visible
 *  `{days}` is a bug report, an empty gap is a mystery. */
export function interpolate(tpl: string, vars?: Vars): string {
  if (!vars) return tpl;
  return tpl.replace(/\{(\w+)\}/g, (_, k) => String(vars[k] ?? `{${k}}`));
}

/** The whole lookup: preferred locale, then the fallback catalogue, then the
 *  key itself so a missing string is identifiable on screen. */
export function translate(
  catalogues: Array<Record<string, unknown> | undefined>,
  key: string,
  vars?: Vars,
): string {
  for (const c of catalogues) {
    const msg = pluralize(getByPath(c, key), vars);
    if (msg != null) return interpolate(msg, vars);
  }
  return key;
}
